import { SpecialZoomLevel, ViewMode, Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { Zoom, fullScreenPlugin } from '@react-pdf-viewer/full-screen';
import '@react-pdf-viewer/full-screen/lib/styles/index.css';
import { getFilePlugin } from '@react-pdf-viewer/get-file';
import { SelectionMode, selectionModePlugin } from '@react-pdf-viewer/selection-mode';
import '@react-pdf-viewer/selection-mode/lib/styles/index.css';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';

interface SafeFormContextProps {
  showFile: (file: File) => void;
}

const FileDialogContext = createContext<SafeFormContextProps | null>(null);

const zoomToPageFit = (zoom: Zoom) => {
  zoom(SpecialZoomLevel.PageFit);
};

export const FileDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const paperRef = useRef<HTMLDivElement | null>(null);

  const fullScreenPluginInstance = fullScreenPlugin({
    onEnterFullScreen: zoomToPageFit,
    onExitFullScreen: zoomToPageFit,
    getFullScreenTarget: (pagesContainer) => paperRef.current ?? pagesContainer,
  });
  const zoomPluginInstance = zoomPlugin();
  const getFilePluginInstance = getFilePlugin();
  const selectionModePluginInstance = selectionModePlugin({ selectionMode: SelectionMode.Hand });

  const { EnterFullScreenButton } = fullScreenPluginInstance;
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;
  const { DownloadButton } = getFilePluginInstance;
  const { SwitchSelectionModeButton } = selectionModePluginInstance;

  const [file, setFile] = useState<File | undefined>();

  const fileUrl = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);

  useEffect(() => {
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl]);

  const showFile = useCallback((f: File) => {
    setFile(f);
  }, []);

  const closeFile = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    }
    setFile(undefined);
  }, []);

  return (
    <FileDialogContext.Provider value={{ showFile }}>
      {children}

      <Dialog
        open={!!file}
        onClose={closeFile}
        container={document.getElementById('page-root')}
        keepMounted
        hideBackdrop
        PaperProps={{ ref: paperRef }}
        sx={{
          right: '20rem',
          position: 'absolute',

          '& .MuiPaper-root': {
            padding: '2rem',
            width: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            height: 'calc(100vh - 150px)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogActions sx={{ padding: 0, flexShrink: 0 }}>
          <Stack
            alignItems="center"
            justifyContent="center"
            direction="row"
            gap="0.5rem"
            sx={{ width: '100%' }}
          >
            <SwitchSelectionModeButton mode={SelectionMode.Hand} />
            <SwitchSelectionModeButton mode={SelectionMode.Text} />

            <Divider orientation="vertical" flexItem />

            <ZoomOutButton />
            <ZoomPopover />
            <ZoomInButton />

            <Divider orientation="vertical" flexItem />

            <EnterFullScreenButton />

            <Divider orientation="vertical" flexItem />

            <DownloadButton />
          </Stack>
        </DialogActions>

        <Divider sx={{ marginBottom: '1rem', flexShrink: 0 }} />

        <DialogContent
          sx={{
            padding: 0,
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <IconButton
            onClick={closeFile}
            sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>

          {!!file && !!fileUrl && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                height: '100%',
                width: '100%',
                position: 'relative',
                '& .rpv-core__viewer': {
                  height: '100%',
                  width: '100%',
                },
              }}
            >
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                <Viewer
                  fileUrl={fileUrl}
                  plugins={[
                    selectionModePluginInstance,
                    getFilePluginInstance,
                    zoomPluginInstance,
                    fullScreenPluginInstance,
                  ]}
                  defaultScale={SpecialZoomLevel.PageFit}
                  viewMode={ViewMode.SinglePage}
                />
              </Worker>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </FileDialogContext.Provider>
  );
};

export const useFileDialog = () => {
  const context = useContext(FileDialogContext);
  if (!context) {
    throw new Error('useFileDialog must be used within a FileDialogProvider');
  }
  return context;
};
